import { useState } from "react";
import type { ChangeEvent, FormEvent } from "react";
import { useTranslation } from "react-i18next";
import "./ChangeUserPassword.css";
import { changePassword, getSessionUser } from "services/users/auth";
import { Navigate } from "react-router-dom";
import Page from "./../../components/page/PageContent/PageContent";
import Button from "components/ui/Button";
import ApiErrorBanner from "components/ApiErrorBanner";
import ErrorBanner from "components/ui/ErrorBanner";
import assert from "utils/assert";

type Field = "old" | "password" | "confirmPassword";

export default function ChangeUserPassword() {
  const { t } = useTranslation();
  const [old, setOld] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState<unknown>("");
  const [passwordsMismatch, setPasswordsMismatch] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [touched, setTouched] = useState<Record<Field, boolean>>({
    old: false,
    password: false,
    confirmPassword: false,
  });
  const [redirect, setRedirect] = useState(false);

  const handleBlur = (field: Field) => () => {
    setTouched(previous => ({ ...previous, [field]: true }));
  };

  const isInvalid = (field: Field, value: string) =>
    value.length === 0 && (touched[field] || submitted);

  const changeUserPassword = (event: FormEvent) => {
    event.preventDefault();
    setSubmitted(true);
    setErrors("");
    setPasswordsMismatch(false);

    const isFormValid =
      old.length > 0 && password.length > 0 && confirmPassword.length > 0;
    if (!isFormValid || password !== confirmPassword) {
      if (password !== confirmPassword) {
        setPasswordsMismatch(true);
      }
      return;
    }

    const { email } = getSessionUser(true).profile;
    assert(email !== null, "signed in without an email address");

    changePassword({ old_passwd: old, new_passwd: password, email }).then(
      () => setRedirect(true),
      respBody => setErrors(respBody)
    );
  };

  if (redirect) {
    return <Navigate to="/" replace />;
  }

  return (
    <Page title={t("Settings.changePassword.title")} name="change-password">
      <form onSubmit={changeUserPassword}>
        <div className="width-100">
          <label htmlFor="old" className="width-30">
            * {t("Settings.changePassword.oldPassword")}
          </label>
          <input
            name="old"
            id="old"
            type="password"
            value={old}
            onBlur={handleBlur("old")}
            className={isInvalid("old", old) ? "error" : ""}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              setOld(event.target.value)
            }
          />
        </div>
        <div className="width-100">
          <label htmlFor="password" className="width-30">
            * {t("Settings.changePassword.newPassword")}
          </label>
          <input
            name="password"
            id="password"
            type="password"
            value={password}
            onBlur={handleBlur("password")}
            className={isInvalid("password", password) ? "error" : ""}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              setPassword(event.target.value)
            }
          />
        </div>
        <div className="width-100">
          <label htmlFor="confirm" className="width-30">
            * {t("Settings.changePassword.confirmNewPassword")}
          </label>
          <input
            name="confirm"
            id="confirm"
            type="password"
            value={confirmPassword}
            onBlur={handleBlur("confirmPassword")}
            className={isInvalid("confirmPassword", confirmPassword) ? "error" : ""}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              setConfirmPassword(event.target.value)
            }
          />
        </div>
        {passwordsMismatch && (
          <ErrorBanner>{t("Settings.changePassword.passwordsDoNotMatch")}</ErrorBanner>
        )}
        <ApiErrorBanner error={errors} />

        <div className="actions">
          <Button onClick={changeUserPassword}>
            {t("Settings.changePassword.submit")}
          </Button>
        </div>
      </form>
    </Page>
  );
}
